import React, { useRef, useState, useEffect, useCallback } from 'react';
import ProductCard from '../../components/common/ProductCard/ProductCard';
import { CATEGORIES } from '../../utils/constants';
import styles from './HomePage.module.css';

// ── Pagination config ──────────────────────────────────────────────────────────
const ADS_PER_PAGE = 100; // Max ads per page

// How many category pills to show before "More" kicks in
const VISIBLE_COUNT = {
  xl: 7,  // > 1200px
  lg: 6,  // 900–1200px
  md: 4,  // 640–900px
};

const HomePage = ({ 
  products, 
  loading, 
  searchTerm,
  activeCategory,
  setActiveCategory,
  handleSearchFocus,
  openProductDetail,
  openPostAdModal,
  headerBg 
}) => {
  const categoryRef = useRef(null);
  const productsRef = useRef(null);
  const moreRef     = useRef(null);

  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [moreDropdownOpen,     setMoreDropdownOpen]     = useState(false);
  const [visibleCategories,    setVisibleCategories]    = useState([]);
  const [hiddenCategories,     setHiddenCategories]     = useState([]);

  // ── Pagination state ───────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ NEW: Animation states for hero section
  const [isHeroVisible, setIsHeroVisible] = useState(false);

  // Reset to page 1 whenever search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeCategory]);

  // ✅ NEW: Trigger hero animations on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHeroVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // (URL restoration and popstate are handled by App.jsx — no duplication needed here)

  // ── Calculate visible vs hidden based on screen width ─────────────────────
  const calculateVisibleCategories = useCallback(() => {
    const width = window.innerWidth;
    let count;
    if      (width > 1200) count = VISIBLE_COUNT.xl;
    else if (width > 900)  count = VISIBLE_COUNT.lg;
    else if (width > 640)  count = VISIBLE_COUNT.md;
    else {
      setVisibleCategories(CATEGORIES);
      setHiddenCategories([]);
      return;
    }
    setVisibleCategories(CATEGORIES.slice(0, count));
    setHiddenCategories(CATEGORIES.slice(count));
  }, []);

  useEffect(() => {
    calculateVisibleCategories();
    window.addEventListener('resize', calculateVisibleCategories);
    return () => window.removeEventListener('resize', calculateVisibleCategories);
  }, [calculateVisibleCategories]);

  // ── Close More dropdown when clicking outside ──────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Category click ─────────────────────────────────────────────────────────
  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setMoreDropdownOpen(false);
    setCategoryDropdownOpen(false);
    setTimeout(() => {
      if (categoryRef.current) {
        const y = categoryRef.current.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  // ── Filter products ────────────────────────────────────────────────────────
  const filteredProducts = products.filter((p) => {
    const matchesSearch   = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // ── Pagination calculations ────────────────────────────────────────────────
  const totalPages    = Math.ceil(filteredProducts.length / ADS_PER_PAGE);
  const pageStart     = (currentPage - 1) * ADS_PER_PAGE;
  const pageEnd       = pageStart + ADS_PER_PAGE;
  const pagedProducts = filteredProducts.slice(pageStart, pageEnd);

  // ── Page change handler — scrolls back to products section ────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    // Scroll to top of products grid smoothly
    setTimeout(() => {
      if (productsRef.current) {
        const y = productsRef.current.getBoundingClientRect().top + window.pageYOffset - 20;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 50);
  };

  // ── Build page number buttons (max 5 visible at a time) ───────────────────
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, 5);
    } else if (currentPage >= totalPages - 2) {
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
    }
    return pages;
  };

  const isHiddenActive = hiddenCategories.includes(activeCategory);

  return (
    <>
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className={`${styles.hero} ${isHeroVisible ? styles.heroVisible : ''}`}>
        <div className={`${styles.heroContent} ${isHeroVisible ? styles.heroContentVisible : ''}`}>
          <h1 className={styles.heroTitle}>Buy & Sell Easily!</h1>
          <p className={styles.heroDescription}>
            Discover great deals or list your own products today. Join thousands of buyers and sellers in Kenya's fastest-growing marketplace.
          </p>
          <div className={styles.heroActions}>
            <button className={`btn btn-primary ${styles.heroBtnPrimary}`} onClick={handleSearchFocus}>
              Get Started
            </button>
            <button className={`btn btn-outline ${styles.heroBtnOutline}`} onClick={openPostAdModal}>
              Post an Ad
            </button>
          </div>
        </div>
        <img 
          src={headerBg} 
          alt="Hero" 
          className={`${styles.heroImg} ${isHeroVisible ? styles.heroImgVisible : ''}`} 
        />
      </section>

      {/* ── Featured Categories ────────────────────────────────────────── */}
      <section className={styles.section} ref={categoryRef} data-category-section>
        <h2 className={styles.sectionTitle}>Featured Categories</h2>

        {/* ── Desktop & Tablet ─────────────────────────────────────────── */}
        <div className={`${styles.categoriesContainer} ${styles.desktopTabletCategories}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>

            {/* Scrollable strip — only the visible pills, no More button here */}
            <div className={styles.categoriesScroll} style={{ flex: 1 }}>
              {visibleCategories.map((cat) => (
                <div
                  key={cat}
                  className={`${styles.categoryPill} ${activeCategory === cat ? styles.active : ''}`}
                  onClick={() => handleCategoryClick(cat)}
                >
                  {cat}
                </div>
              ))}
            </div>

            {/* More trigger + dropdown — OUTSIDE the overflow:auto scroll strip */}
            {hiddenCategories.length > 0 && (
              <div ref={moreRef} className={styles.moreDropdown}>
                <div
                  className={`${styles.categoryPill} ${styles.moreButton} ${isHiddenActive ? styles.active : ''}`}
                  onClick={() => setMoreDropdownOpen(prev => !prev)}
                >
                  {isHiddenActive ? activeCategory : 'More'}
                  <svg
                    width="14" height="14" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    style={{
                      transition: 'transform 0.2s',
                      transform: moreDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      flexShrink: 0,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                {moreDropdownOpen && (
                  <div className={styles.moreDropdownMenu}>
                    {hiddenCategories.map((cat) => (
                      <div
                        key={cat}
                        className={`${styles.moreDropdownItem} ${activeCategory === cat ? styles.active : ''}`}
                        onClick={() => handleCategoryClick(cat)}
                      >
                        {cat}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile Categories ─────────────────────────────────────────── */}
        <div className={`${styles.categoryMobileWrapper} ${styles.mobileCategories}`}>
          <div className={styles.categoryPillsRow}>
            <div
              className={`${styles.categoryPill} ${activeCategory === 'All' ? styles.active : ''}`}
              onClick={() => handleCategoryClick('All')}
            >
              All
            </div>
            <div className={styles.categoryDropdown}>
              <button
                className={styles.categoryDropdownBtn}
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              >
                <span>{activeCategory !== 'All' ? activeCategory : 'Select Category'}</span>
                <svg
                  width="20" height="20" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2"
                  style={{
                    transform: categoryDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {categoryDropdownOpen && (
                <div className={styles.categoryDropdownMenu}>
                  {CATEGORIES.filter(cat => cat !== 'All').map((cat) => (
                    <div
                      key={cat}
                      className={`${styles.categoryDropdownItem} ${activeCategory === cat ? styles.active : ''}`}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Products grid ──────────────────────────────────────────────── */}
      <section className={styles.section} ref={productsRef}>
        {loading ? (
          <div className={styles.loadingMessage}>
            <div className={styles.spinner} />
            <p>Loading amazing deals...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className={styles.noAdsMessage}>
            <svg className={styles.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8"  x2="12"   y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3>No ads found</h3>
            <p>Be the first to post in this category!</p>
            <button className="btn btn-primary" onClick={openPostAdModal}>Post Your Ad</button>
          </div>
        ) : (
          <>
            {/* ── Results info ── */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 16, flexWrap: 'wrap', gap: 8,
            }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>
                Showing <strong>{pageStart + 1}–{Math.min(pageEnd, filteredProducts.length)}</strong> of{' '}
                <strong>{filteredProducts.length}+</strong> ads
                {activeCategory !== 'All' && (
                  <span style={{ color: '#6366f1', marginLeft: 4 }}>in {activeCategory}</span>
                )}
              </span>
              {totalPages > 1 && (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>

            {/* ── Products grid ── */}
            <div className={styles.productsGrid}>
              {pagedProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onClick={() => openProductDetail(p)}
                />
              ))}
            </div>

            {/* ── Pagination controls ── */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, marginTop: 40, flexWrap: 'wrap',
              }}>
                {/* Previous */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    width: 38, height: 38, borderRadius: 8, border: '1.5px solid #e5e7eb',
                    background: currentPage === 1 ? '#f9fafb' : '#fff',
                    color: currentPage === 1 ? '#d1d5db' : '#374151',
                    fontSize: 16, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (currentPage !== 1) e.currentTarget.style.borderColor = '#6366f1'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >‹</button>

                {/* Page numbers */}
                {getPageNumbers().map((n) => (
                  <button
                    key={n}
                    onClick={() => handlePageChange(n)}
                    style={{
                      width: 38, height: 38, borderRadius: 8,
                      border: `1.5px solid ${currentPage === n ? '#6366f1' : '#e5e7eb'}`,
                      background: currentPage === n ? '#6366f1' : '#fff',
                      color: currentPage === n ? '#fff' : '#374151',
                      fontSize: 14, fontWeight: currentPage === n ? 700 : 500,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { if (currentPage !== n) { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.color = '#6366f1'; } }}
                    onMouseLeave={e => { if (currentPage !== n) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; } }}
                  >{n}</button>
                ))}

                {/* Next */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    width: 38, height: 38, borderRadius: 8, border: '1.5px solid #e5e7eb',
                    background: currentPage === totalPages ? '#f9fafb' : '#fff',
                    color: currentPage === totalPages ? '#d1d5db' : '#374151',
                    fontSize: 16, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (currentPage !== totalPages) e.currentTarget.style.borderColor = '#6366f1'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                >›</button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default HomePage;

